import React, { Component } from 'react';
import classNames from 'classnames';
import PropTypes from 'prop-types';
import Truncate from 'react-truncate';
import { FormattedMessage } from 'react-intl';

// Components
import { Container, Col, Row, CardBody, CardTitle } from 'reactstrap';
import Slider from 'react-slick';
import Media from 'react-media';
import Alert from 'components/atoms/Alert';
import Panel from 'components/atoms/Panel';
import Button from 'components/atoms/Button';
import VehicleBuilderList from 'components/molecules/VehicleBuilderList';
import MileageBuilder from 'components/molecules/MileageBuilder';
import RegistrationDateBuilder from 'components/molecules/RegistrationDateBuilder';
import { Icon } from 'components/atoms/Icon/';
import SliderIconWrapper from 'components/pages/VehiclePage/components/SliderIconWrapper';
import AppointmentInfo from 'components/molecules/AppointmentInfo/';

// Utils
import { isObjEmpty } from 'utils/varValidation';
import { formatPhoneNumber } from 'utils/string';
import {
  getTabElements,
  goNextTabElement,
  getCardFocus
} from 'utils/adaConfigurations/VehicleBuilderADA';

// Constants
import { APP_BREAKPOINTS } from 'constants/appConfig';
import {
  VEHICLE_PROPERTIES,
  VEHICLE_TRIM,
  LANES,
  LANES_WITHOUT_TRIM
} from 'constants/vehicle';
import { APP_FLOW } from 'constants/appFlow';
import {
  HOME_ROUTE,
  USER_VEHICLES_ROUTE,
  SERVICES_ROUTE,
  APPOINTMENT_ROUTE,
  VEHICLE_ROUTE
} from 'constants/routes';
import { VEHICLE_ADA } from 'constants/vehicleADA';

import './VehicleBuilder.scss';
import { resetScrollPosition } from 'utils/DOM';

const ns = 'vehicle-builder';
const { MAKE, YEAR, MODEL, MILEAGE } = VEHICLE_PROPERTIES;
const {
  TRIM,
  TRANSMISSION,
  DRIVE_TYPE,
  ENGINE_TYPE,
  ENGINE_SIZE
} = VEHICLE_TRIM;

const { FIND_ME, LOG_ME_IN, QUICK_APPT } = APP_FLOW;

const {
  FORWARD,
  BACKWARD,
  ADA_CLASS,
  CARD_BODY,
  HEADER_ARROW_BACK
} = VEHICLE_ADA;

class VehicleBuilder extends Component {
  constructor(props) {
    super(props);
    // prettier-ignore
    this.state = {
      isMobile: null
    };
    this.sliker = React.createRef();
    this.currentSlide = 0;
  }

  componentDidMount() {
    this.onResize();
    window.addEventListener('resize', this.onResize.bind(this));
    const root = document.getElementById('root');
    const { showLastLane } = this.props;
    const {
      vehicle,
      setIdkSelected,
      vehicleEditableEnabled,
      trims,
      setExactMileageFlag
    } = this.props;
    const sliker = this.sliker.current;
    root && root.addEventListener('keydown', this.handleKeyDown, true);
    setIdkSelected(false);
    if (!sliker) {
      return;
    }
    if (vehicleEditableEnabled) {
      setExactMileageFlag(0);
      if (vehicle.transmission) {
        this.carouselGoTo(ENGINE_SIZE);
      } else if (vehicle.model) {
        this.carouselGoTo(TRANSMISSION);
      } else if (vehicle.year) {
        this.carouselGoTo(MODEL);
      } else if (vehicle.make) {
        this.carouselGoTo(YEAR);
      }
    } else if (showLastLane) {
      this.carouselGoTo(MILEAGE);
    } else {
      this.carouselGoTo(trims.length < 1 ? MODEL : ENGINE_SIZE);
    }
    this.autoSelectItem();
    getTabElements(vehicleEditableEnabled);
    resetScrollPosition(0);
  }

  componentDidUpdate() {
    this.autoSelectItem();
  }

  /* eslint-disable react/sort-comp */
  componentWillUnmount() {
    window.removeEventListener('resize', this.onResize.bind(this));
  }
  /* eslint-enable react/sort-comp */

  handleKeyDown = (e: Object) => {
    const { vehicleEditableEnabled, currentPath } = this.props;
    const className = e.target.className;
    if (currentPath !== VEHICLE_ROUTE) {
      return;
    }
    if (e.shiftKey && e.keyCode === 9) {
      // going back in tab flow
      getTabElements(vehicleEditableEnabled);
      if (
        className.indexOf(CARD_BODY) !== -1 ||
        className.indexOf('slick-arrow slick-next') !== -1
      ) {
        goNextTabElement(BACKWARD, e, vehicleEditableEnabled);
      }
    } else if (e.keyCode === 9) {
      // going forward in tab flow
      getTabElements(vehicleEditableEnabled);
      if (
        className.indexOf('slick-arrow slick-prev') !== -1 ||
        className.indexOf('slick-arrow slick-next') !== -1 ||
        className.indexOf(ADA_CLASS) !== -1 ||
        className.indexOf(CARD_BODY) !== -1 ||
        className.indexOf(HEADER_ARROW_BACK) !== -1
      ) {
        goNextTabElement(FORWARD, e, vehicleEditableEnabled);
      }
    }
  };

  onResize = () => {
    // TODO: bug on resize
  };

  autoSelectItem = () => {
    const {
      makes,
      years,
      models,
      trims,
      transmissions,
      driveTypes,
      engineTypes,
      engineSizes,
      trimsData,
      vehicle: {
        make,
        year,
        model,
        trim,
        transmission,
        driveType,
        engineType,
        engineSize
      },
      isIdkSelected,
      vehicleEditableEnabled
    } = this.props;
    const recordsArray = [
      makes,
      years,
      models,
      transmissions,
      driveTypes,
      trims,
      engineTypes,
      engineSizes
    ].filter(record => Boolean(record.length));
    const lastRecordPosition = recordsArray.length - 1;
    const selectionArrays = [
      make,
      year,
      model,
      transmission,
      driveType,
      trim,
      engineType,
      engineSize
    ];
    const type = LANES[lastRecordPosition];
    if (!recordsArray[lastRecordPosition] || isIdkSelected) {
      return;
    }
    const lastRecord = recordsArray[lastRecordPosition].filter(item =>
      Boolean(item.data)
    );
    if (
      lastRecord.length === 1 &&
      !selectionArrays[lastRecordPosition] &&
      vehicleEditableEnabled
    ) {
      this.onOptionSelected(lastRecord[0].data, type);
    } else {
      if (
        (type === ENGINE_SIZE && selectionArrays[lastRecordPosition]) ||
        (!isObjEmpty(trimsData) && !trimsData.transmissions)
      ) {
        this.carouselGoTo(MILEAGE);
        return;
      }
      setTimeout(() => this.carouselGoTo(type), 600);
    }
  };

  carouselGoTo = type => {
    const sliker = this.sliker.current;
    const { isMobile } = this.state;
    const {
      setShowMobileFooter,
      appFlow,
      vehiclePageRevisit,
      showLastLane,
      showTrimsLane
    } = this.props;
    if (!sliker || isMobile === null) {
      return;
    }
    if (isMobile && !showLastLane && type === MILEAGE && !vehiclePageRevisit) {
      this.skipHiddenMileage();
    }
    const { currentSlide } = sliker.innerSlider.state;
    const { breakpoint } = sliker.state;
    const lanes = showTrimsLane ? LANES : LANES_WITHOUT_TRIM;
    const lastSlide = 7;
    let renderedOtherLanes;
    let goToType = type;
    if (!isMobile && type === MILEAGE) {
      goToType = ENGINE_SIZE;
    }
    if (breakpoint) {
      if (breakpoint === APP_BREAKPOINTS.XS) {
        renderedOtherLanes = 0;
      } else {
        renderedOtherLanes = showLastLane ? 1 : 2;
      }
    } else {
      renderedOtherLanes = showLastLane ? 2 : 3;
    }
    const nextSlide = lanes.indexOf(goToType) - renderedOtherLanes;
    if (nextSlide > currentSlide) {
      sliker.slickGoTo(nextSlide);
      resetScrollPosition(0);
      if (nextSlide >= lastSlide) {
        setShowMobileFooter(true);
      }
    } else {
      if (isMobile && appFlow === QUICK_APPT && type === MODEL) {
        this.skipHiddenMileage();
      }
    }
  };

  onOptionSelected = async (itemSelected, type) => {
    const {
      selectMake,
      selectYear,
      selectModel,
      selectTrim,
      selectTransmission,
      selectDriveType,
      selectEngineType,
      selectEngineSize,
      selectQuickApptOptions,
      setVehiclePageRevisit,
      appFlow,
      localizedQuickApptService,
      sendVehicleStart,
      vehicle,
      intl
    } = this.props;
    const { isMobile } = this.state;
    setVehiclePageRevisit(false);
    switch (type) {
      case MAKE:
        if (!vehicle.make) {
          // Check if make was not previously selected
          sendVehicleStart();
        }
        selectMake(itemSelected);
        break;
      case YEAR:
        selectYear(itemSelected);
        break;
      case MODEL:
        await selectModel(itemSelected, intl);
        if (appFlow === QUICK_APPT) {
          await selectQuickApptOptions(localizedQuickApptService);
        }
        break;
      case TRIM:
        selectTrim(itemSelected);
        break;
      case TRANSMISSION:
        selectTransmission(itemSelected);
        break;
      case DRIVE_TYPE:
        selectDriveType(itemSelected);
        break;
      case ENGINE_TYPE:
        selectEngineType(itemSelected);
        break;
      case ENGINE_SIZE:
        selectEngineSize(itemSelected);
        break;
      default:
        break;
    }
    if (itemSelected === '' && isMobile) {
      this.onSkip();
    }
  };

  getSubtitle = () => {
    const { vehicle, intl } = this.props;
    const vehicleInfoArray = [
      vehicle.make,
      vehicle.year,
      vehicle.model,
      vehicle.trim,
      vehicle.engineType
    ]
      .filter(Boolean)
      .filter(vp => vp !== 'UNKNOWN');
    if (vehicleInfoArray.length > 0) {
      const formattedLabel = intl.formatMessage({
        id: 'c8.vehicle.page.other'
      });
      return vehicleInfoArray.join(' · ').replace(/OTHER/g, formattedLabel);
    }
    return intl.formatMessage({
      id: 'c8.vehicle.page.tell.us.about.your.vehicle'
    });
  };

  getPanelTitle = section => {
    return (
      <div>
        <div className={`${ns}__vehicle-title d-none d-sm-block`}>
          {section.toUpperCase()}
        </div>
        <div className={`${ns}__vehicle-title d-block d-sm-none`}>
          {this.getSubtitle()}
        </div>
      </div>
    );
  };

  /**
   * Mobile Behavior
   * @param e Event
   */
  onGoBack = e => {
    const sliker = this.sliker.current;
    if (!sliker) {
      return;
    }
    const { currentSlide } = sliker.innerSlider.state;
    const {
      restartVehicleFrom,
      appFlow,
      goTo,
      engineTypes,
      vehicleEditableEnabled,
      setShowMobileFooter,
      setIdkSelected,
      showTrimsLane
    } = this.props;
    let adjustHiddenTrimLane = 0;

    if (!showTrimsLane) {
      adjustHiddenTrimLane++;
    }
    // move to the start page if the user clicks back when makes is visible
    if (currentSlide === 0) {
      if (appFlow === LOG_ME_IN && vehicleEditableEnabled) {
        return goTo(USER_VEHICLES_ROUTE);
      } else {
        return goTo(HOME_ROUTE);
      }
    }
    if (appFlow === LOG_ME_IN && !vehicleEditableEnabled) {
      return goTo(USER_VEHICLES_ROUTE);
    }
    if (LANES.indexOf(MILEAGE) - adjustHiddenTrimLane === currentSlide) {
      if (appFlow === FIND_ME) {
        goTo(HOME_ROUTE);
        return;
      }
      if (!engineTypes.length) {
        setIdkSelected(false);
        restartVehicleFrom(TRANSMISSION);
        sliker.slickGoTo(LANES.indexOf(TRANSMISSION));
        setShowMobileFooter(false);
        return;
      }
    }
    sliker.slickGoTo(currentSlide - 1);
    setShowMobileFooter(false);
  };

  getLane = (section, listClassName, body, disabled, type) => {
    // a col is needed in desktop but in mobile the additional padding is not
    // needed (it's provided by a .container up in the tree, __list-container
    // removes this additional panel only on xs)
    const cardBodyClassProps = classNames({
      [`${ns}__card-body--disabled`]: disabled
    });
    const { intl } = this.props;
    let cardVehicleAriaLabel;
    switch (type) {
      case MAKE:
        cardVehicleAriaLabel = intl.formatMessage({
          id: 'c8.aria.select.your.make',
          defaultMessage: 'Select your make'
        });
        break;
      case YEAR:
        cardVehicleAriaLabel = intl.formatMessage({
          id: 'c8.aria.select.vehicle.year',
          defaultMessage: 'Select vehicle year'
        });
        break;
      case MODEL:
        cardVehicleAriaLabel = intl.formatMessage({
          id: 'c8.aria.select.model',
          defaultMessage: 'Select model'
        });
        break;
      case TRIM:
        cardVehicleAriaLabel = intl.formatMessage({
          id: 'c8.aria.select.trim',
          defaultMessage: 'Select trim'
        });
        break;
      case TRANSMISSION:
        cardVehicleAriaLabel = intl.formatMessage({
          id: 'c8.aria.select.transmission',
          defaultMessage: 'Select transmission'
        });
        break;
      case DRIVE_TYPE:
        cardVehicleAriaLabel = intl.formatMessage({
          id: 'c8.aria.select.drive.type',
          defaultMessage: 'Select drive type'
        });
        break;
      case ENGINE_TYPE:
        cardVehicleAriaLabel = intl.formatMessage({
          id: 'c8.aria.select.engine.type',
          defaultMessage: 'Select engine type'
        });
        break;
      case ENGINE_SIZE:
        cardVehicleAriaLabel = intl.formatMessage({
          id: 'c8.aria.select.engine.size',
          defaultMessage: 'Select engine size'
        });
        break;
      default:
        break;
    }
    return (
      <Col className={`${ns}__list-container`}>
        <Panel className={listClassName}>
          <CardBody
            id={`vehicle_${section.toLowerCase()}_container`}
            className={`${section.toLowerCase()}`}
            role={'group'}
            aria-label={cardVehicleAriaLabel}
          >
            <CardTitle tag={'h2'}>{this.getPanelTitle(section)}</CardTitle>
            <div className={cardBodyClassProps} />
            {body()}
          </CardBody>
        </Panel>
      </Col>
    );
  };

  getList = (section, listClassName, record, type, itemSelected, serviceId) => {
    const {
      transmissions,
      hasIDK,
      isIDK,
      gtmIsIDK,
      vehicleEditableEnabled,
      intl,
      showTrimsLane
    } = this.props;
    const trimsIDs = [
      'transmission',
      'driveType',
      'trims',
      'engineType',
      'engineSize'
    ];
    if (!transmissions.length && trimsIDs.find(id => id === serviceId)) {
      return null;
    }
    if (
      !vehicleEditableEnabled &&
      isIDK &&
      trimsIDs.find(id => id === serviceId)
    ) {
      return null;
    }

    if (serviceId === 'trims') {
      if (
        (record.length === 1 && record[0].data === 'UNKNOWN') ||
        !showTrimsLane
      ) {
        return null;
      }
    }

    return this.getLane(
      section,
      listClassName,
      () => (
        <div>
          <VehicleBuilderList
            items={record}
            type={type}
            onSelectionCallback={this.onOptionSelected}
            itemSelected={itemSelected}
            serviceId={serviceId}
            className={`${ns}__lane`}
            onSkipClick={this.onSkip}
          />
          {(type === TRIM ||
            type === TRANSMISSION ||
            type === DRIVE_TYPE ||
            type === ENGINE_TYPE ||
            type === ENGINE_SIZE) &&
            hasIDK && (
              <div className={`${ns}__skip`}>
                <Button
                  className="d-sm-none"
                  buttonTheme="link"
                  onClick={() => {
                    this.onSkip();
                    gtmIsIDK('Skip');
                  }}
                >
                  {intl.formatMessage({ id: 'c8.vehicle.page.skip' })}
                </Button>
              </div>
            )}
        </div>
      ),
      !vehicleEditableEnabled,
      type
    );
  };

  skipHiddenMileage = async () => {
    const {
      loadServices,
      goTo,
      appFlow,
      getApptAdvisorsAndTranspOptions,
      setVehiclePageRevisit
    } = this.props;
    loadServices();
    setVehiclePageRevisit(true);
    if (appFlow === QUICK_APPT) {
      await getApptAdvisorsAndTranspOptions();
      goTo(APPOINTMENT_ROUTE);
    } else {
      goTo(SERVICES_ROUTE);
    }
  };

  onSkip = () => {
    const { validateTrimData } = this.props;
    const sliker = this.sliker.current;
    if (!sliker) {
      return;
    }
    validateTrimData();
    this.carouselGoTo(MILEAGE);
  };

  render() {
    const {
      dealerCountryCode,
      getCustomerPhone,
      getCustomerEmail,
      showLastLane,
      showMileage,
      vehicleEditableEnabled,
      intl
    } = this.props;

    if (!dealerCountryCode) {
      return <div />;
    }
    const {
      makes,
      years,
      models,
      trims,
      transmissions,
      driveTypes,
      engineTypes,
      engineSizes,
      vehicle: {
        model,
        trim,
        transmission,
        driveType,
        engineType,
        engineSize,
        licenseNumber
      },
      isAlertEnabled
    } = this.props;

    // Used for FIND ME flow when phone no. has no vehicles
    let formattedNumber = null;
    if (getCustomerPhone && dealerCountryCode && isAlertEnabled) {
      formattedNumber = formatPhoneNumber(getCustomerPhone, dealerCountryCode);
    }
    const sliderCfg = {
      dots: false,
      infinite: false,
      speed: 500,
      slidesToShow: showLastLane ? 3 : transmissions.length ? 4 : 3,
      slidesToScroll: 1,
      swipeToSlide: false,
      swipe: false,
      afterChange: currentSlide => {
        const previousSlide = this.currentSlide;
        if (!this.state.isMobile) {
          if (currentSlide > previousSlide) {
            getCardFocus(vehicleEditableEnabled, showMileage);
          } else {
            getTabElements(vehicleEditableEnabled);
          }
        }
        this.currentSlide = currentSlide;
      },
      nextArrow: (
        <SliderIconWrapper
          aria-label={intl.formatMessage({
            id: 'c8.aria.advance.to.next.option',
            defaultMessage: 'Advance to next option'
          })}
          type="chevron__right"
        />
      ),
      prevArrow: (
        <SliderIconWrapper
          aria-label={intl.formatMessage({
            id: 'c8.aria.return.to.previous.option',
            defaultMessage: 'Return to previous option'
          })}
          type="chevron__left"
        />
      ),
      responsive: [
        {
          breakpoint: APP_BREAKPOINTS.MD,
          settings: {
            slidesToShow: showLastLane ? 2 : 3,
            slidesToScroll: 1
          }
        },
        {
          breakpoint: APP_BREAKPOINTS.XS,
          settings: {
            slidesToShow: 1,
            slidesToScroll: 1
          }
        }
      ]
    };

    const getCarousel = mobile => {
      const { isMobile } = this.state;
      const { showLastLane, isQuoteAppt } = this.props;
      const {
        intl,
        showMileage,
        showRegDate,
        vehicle: { make, year },
        vehicleEditableEnabled
      } = this.props;
      if (isMobile !== mobile) {
        this.setState({
          isMobile: mobile
        });
      }
      const sliderSize = showLastLane ? '8' : '12';
      const sliderSizeLg = showLastLane ? '9' : '12';
      const lastSlideClassName =
        showRegDate && showMileage
          ? `${ns}__lane--short`
          : `${ns}__lane--last-slide`;
      const panelClassNameMileage =
        showRegDate && showMileage ? `${ns}__short-panel` : '';
      const panelClassNameServiceDate = panelClassNameMileage
        ? panelClassNameMileage + '--service-date'
        : '';
      return (
        <Row noGutters>
          <Col
            xs="12"
            sm={sliderSize}
            md={sliderSize}
            lg={sliderSizeLg}
            className={`${ns}__slider-container`}
          >
            <Slider ref={this.sliker} {...sliderCfg}>
              {this.getList(
                intl.formatMessage({ id: 'c8.vehicle.page.make' }),
                '',
                makes,
                MAKE,
                make,
                'settings'
              )}
              {this.getList(
                intl.formatMessage({ id: 'c8.vehicle.page.year' }),
                '',
                years,
                YEAR,
                year,
                'years'
              )}
              {this.getList(
                intl.formatMessage({ id: 'c8.vehicle.page.model' }),
                '',
                models,
                MODEL,
                model,
                'models'
              )}
              {this.getList(
                intl.formatMessage({ id: 'c8.vehicle.page.transmission' }),
                '',
                transmissions,
                TRANSMISSION,
                transmission,
                'transmission'
              )}
              {this.getList(
                intl.formatMessage({ id: 'c8.vehicle.page.drive.type' }),
                '',
                driveTypes,
                DRIVE_TYPE,
                driveType,
                'driveType'
              )}
              {this.getList(
                intl.formatMessage({ id: 'c8.vehicle.page.trim' }),
                '',
                trims,
                TRIM,
                trim,
                'trims'
              )}
              {this.getList(
                intl.formatMessage({ id: 'c8.vehicle.page.engine.type' }),
                '',
                engineTypes,
                ENGINE_TYPE,
                engineType,
                'engineType'
              )}
              {this.getList(
                intl.formatMessage({ id: 'c8.vehicle.page.engine.size' }),
                '',
                engineSizes,
                ENGINE_SIZE,
                engineSize,
                'engineSize'
              )}
              {mobile &&
                showLastLane &&
                this.getLane(
                  intl.formatMessage({
                    id: 'c8.registration.date.title.in.service.date'
                  }),
                  '',
                  () => (
                    <div className={lastSlideClassName}>
                      {showRegDate &&
                        vehicleEditableEnabled && (
                          <RegistrationDateBuilder key={year} />
                        )}
                      {showMileage && <MileageBuilder />}
                    </div>
                  ),
                  isQuoteAppt
                )}
            </Slider>
          </Col>
          {!mobile &&
            showLastLane && (
              <Col sm="4" md="4" lg="3" className={`${ns}__mileage-container`}>
                {showRegDate &&
                  this.getLane(
                    intl.formatMessage({
                      id: 'c8.registration.date.title.in.service.date'
                    }),
                    panelClassNameServiceDate,
                    () => (
                      <div className={lastSlideClassName}>
                        <RegistrationDateBuilder key={year} />
                      </div>
                    ),
                    !vehicleEditableEnabled
                  )}
                {showMileage &&
                  this.getLane(
                    intl.formatMessage({ id: 'c8.vehicle.page.mileage' }),
                    panelClassNameMileage,
                    () => (
                      <div className={lastSlideClassName}>
                        <MileageBuilder />
                      </div>
                    ),
                    isQuoteAppt
                  )}
              </Col>
            )}
        </Row>
      );
    };

    return (
      <div className={ns}>
        {isAlertEnabled && (
          <Alert
            color="warning"
            animation="top-down"
            className={`${ns}__alert`}
          >
            <Truncate lines={1} ellipsis="…">
              <FormattedMessage
                id="c8.alert.could.not.find.any.vehicle.for"
                values={{
                  phoneOrEmail:
                    formattedNumber || getCustomerEmail || licenseNumber
                }}
              >
                {msg => msg}
              </FormattedMessage>
            </Truncate>
          </Alert>
        )}
        <AppointmentInfo
          key="appointmentInfo"
          className={'d-block d-sm-none'}
        />
        <Container className={`${ns}__mobile-go-back d-block d-sm-none`}>
          <Row>
            <Col xs="6" sm="6">
              <div className={`${ns}__deselect-button button-header`}>
                <div onClick={this.onGoBack}>
                  <Icon type="arrow__back" className={`${ns}__icon`} />
                </div>
              </div>
            </Col>
          </Row>
        </Container>
        <Container>
          <Row className={`${ns}__vehicle-builder-title d-none d-sm-block`}>
            <Col>
              <h1 className={`${ns}__aria-main-title`}>
                {intl.formatMessage({
                  id: 'c8.vehicle.page.tell.us.about.your.vehicle'
                })}
              </h1>
            </Col>
          </Row>
        </Container>
        {/*
          px-0 is needed because each child defines it's own left/right padding
          in addition to the one defined by the container
        */}
        <Container className={`px-0`}>
          <Media query={`(max-width: ${APP_BREAKPOINTS.XS}px)`}>
            {matches => getCarousel(matches)}
          </Media>
        </Container>
      </div>
    );
  }
}

VehicleBuilder.propTypes = {
  alert: PropTypes.bool,
  appFlow: PropTypes.string.isRequired,
  dealerCountryCode: PropTypes.string,
  driveTypes: PropTypes.array.isRequired,
  engineSizes: PropTypes.array.isRequired,
  engineTypes: PropTypes.array.isRequired,
  getCustomerEmail: PropTypes.string.isRequired,
  getCustomerPhone: PropTypes.string.isRequired,
  goTo: PropTypes.func.isRequired,
  gtmIsIDK: PropTypes.func.isRequired,
  hasIDK: PropTypes.bool,
  intl: PropTypes.object,
  isAlertEnabled: PropTypes.bool.isRequired,
  isIDK: PropTypes.bool,
  isIdkSelected: PropTypes.bool,
  /**
   * True if the user is logged in
   */
  isLoggedIn: PropTypes.bool.isRequired,
  /**
   * True if the user has partially signed in through a social provider
   */
  isPartiallySignedIn: PropTypes.bool.isRequired,
  loadServices: PropTypes.func.isRequired,
  makes: PropTypes.array.isRequired,
  models: PropTypes.array.isRequired,
  restartVehicleFrom: PropTypes.func.isRequired,
  selectDriveType: PropTypes.func.isRequired,
  selectEngineSize: PropTypes.func.isRequired,
  selectEngineType: PropTypes.func.isRequired,
  selectMake: PropTypes.func.isRequired,
  selectModel: PropTypes.func.isRequired,
  selectTransmission: PropTypes.func.isRequired,
  selectTrim: PropTypes.func.isRequired,
  selectYear: PropTypes.func.isRequired,
  setIdkSelected: PropTypes.func.isRequired,
  setShowMobileFooter: PropTypes.func.isRequired,
  setVehiclePageRevisit: PropTypes.func.isRequired,
  showMileage: PropTypes.bool.isRequired,
  showLastLane: PropTypes.bool.isRequired,
  vehiclePageRevisit: PropTypes.bool.isRequired,
  showRegDate: PropTypes.bool,
  showTrimsLane: PropTypes.bool.isRequired,
  transmissions: PropTypes.array.isRequired,
  trims: PropTypes.array.isRequired,
  trimsData: PropTypes.object.isRequired,
  validateTrimData: PropTypes.func.isRequired,
  vehicle: PropTypes.object.isRequired,
  vehicleEditableEnabled: PropTypes.bool.isRequired,
  years: PropTypes.array.isRequired,
  selectQuickApptOptions: PropTypes.func.isRequired,
  sendVehicleStart: PropTypes.func.isRequired,
  localizedQuickApptService: PropTypes.string,
  currentPath: PropTypes.string,
  isQuoteAppt: PropTypes.bool.isRequired
};

export default VehicleBuilder;



// WEBPACK FOOTER //
// ./src/components/organisms/VehicleBuilder/VehicleBuilder.js